import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Component for a location input search bar with dropdown options.
 * Handles user selection of locations and displays corresponding icons.
 *
 * Props:
 * @param setLocationError Function to set location error state
 * @param locationError State indicating presence of location error
 * @param selectedLocation Array containing selected location details
 * @param setSelectedLocation Function to set selected location details
 * @param handleLocationChange Function to handle location change
 * @param locations Array of available locations
 * @param setLocations Function to set list of locations
 * @param type Type of location input (e.g., "pickup" or "return")
 * @param openModal Function to open modal for selecting locations
 * @param userCountry ISO country code for the current user (used to pick classic/new search)
 */
import { useEffect, useRef, useState } from "react";
import { Constant } from "../../../../domain/constants";
import { useTranslation } from "react-i18next";
import IconClosed from "../../../assets/iconClosed";
// Styles
import "../../../assets/saas/organisms/_searchbar.scss";
import IconAirportBlack from "../../../assets/iconAirportBlack";
import IconLocationBlack from "../../../assets/iconLocationBlack";
import IconPortBlack from "../../../assets/iconPortBlack";
import clsx from "clsx";
import { isTestMarketCountry } from "../../../../utils/testMarket";
import { findTranslation } from "../../../../infrastructure/i18n/findTranslation";
import { getUrlLocale } from "../../../../utils/getUrlLocale";
const LocationInputSearchBar = ({ setLocationError, locationError, selectedLocation, setSelectedLocation, handleLocationChange, locations, setLocations, type, openModal, userCountry = "US", }) => {
    const isTestMarket = isTestMarketCountry(userCountry);
    const [noResults, setNoResults] = useState(false);
    const [isFocused, setIsFocused] = useState(true);
    const [isClickedOutside, setIsClickedOutside] = useState(false);
    const refDiv = useRef(null);
    const { t } = useTranslation();
    // Effect hook to handle clicks outside the component
    useEffect(() => {
        const handleDocumentClick = (event) => {
            if (refDiv.current && !refDiv.current.contains(event.target)) {
                setIsClickedOutside(true);
                handleValidation();
            }
            else {
                setIsClickedOutside(false);
            }
        };
        document.addEventListener("click", handleDocumentClick);
        return () => {
            document.removeEventListener("click", handleDocumentClick);
        };
    }, [selectedLocation, selectedLocation]);
    // Validate the location selection and set to default if empty
    const handleValidation = () => {
        if (selectedLocation[1] === "" && selectedLocation[0].trim() === "") {
            setSelectedLocation(["", "", Constant.DEFAULT]);
        }
    };
    // when the element unmounts, set the location to the last location if the actual selected location is empty
    // Function to capitalize the first letter of each word in a text
    const capitalizeFirstLetterAfterSpace = (text) => {
        return text;
    };
    // Capitalize the first letter of the selected location
    selectedLocation[0] = capitalizeFirstLetterAfterSpace(selectedLocation[0]);
    // WI #8320 — Pick translation by URL locale, NOT by `i18next.language`.
    // `findTranslation` performs case-insensitive exact match, then falls back
    // to a 2-letter prefix (so `en-GB` -> `en`). When the URL locale is not
    // represented in the API `translations[]` (which only contains ES/IT/FR/DE/PT),
    // we fall back to `location.name` / `location.description`.
    const checkTranlation = (location) => {
        var _a, _b, _c;
        let nameTranslation = (_b = (_a = location === null || location === void 0 ? void 0 : location.name) !== null && _a !== void 0 ? _a : location === null || location === void 0 ? void 0 : location.description) !== null && _b !== void 0 ? _b : "";
        if (isTestMarket) {
            return (_c = location === null || location === void 0 ? void 0 : location.description) !== null && _c !== void 0 ? _c : "";
        }
        if (!Array.isArray(location === null || location === void 0 ? void 0 : location.translations)) {
            return nameTranslation;
        }
        const match = findTranslation(location.translations, getUrlLocale("lower"));
        if (match && typeof match.name === "string" && match.name.trim() !== "") {
            nameTranslation = match.name;
        }
        if (typeof nameTranslation === "string" && nameTranslation.length > 0) {
            return nameTranslation.charAt(0).toUpperCase() + nameTranslation.slice(1);
        }
        return "";
    };
    const openModalEvent = (type) => {
        openModal(type);
        handleEditSearchBar();
    };
    const handleEditSearchBar = () => {
        if (window.location.href.indexOf("collections") > -1) {
            document.body.classList.add("editbar-collection");
        }
    };
    const handleLocationSelect = (location) => {
        const normalizeStorageValue = (value) => {
            if (value == null)
                return "";
            const stringValue = String(value).trim();
            if (stringValue === "" ||
                stringValue.toLowerCase() === "undefined" ||
                stringValue.toLowerCase() === "null") {
                return "";
            }
            return stringValue;
        };
        const setSessionValue = (key, value) => {
            const normalized = normalizeStorageValue(value);
            if (normalized) {
                sessionStorage.setItem(key, normalized);
            }
            else {
                sessionStorage.removeItem(key);
            }
        };
        // Immediately uncheck the CSS filter-switch to prevent blank dropdown flash
        const filterSwitchId = type === "pickup" ? "filter-switch" : "filter-switch-1";
        const filterSwitch = document.getElementById(filterSwitchId);
        if (filterSwitch)
            filterSwitch.checked = false;
        setSelectedLocation(isTestMarket
            ? [location.description, location.description, location.placeType]
            : [checkTranlation(location), location.code, location.location_type]);
        setLocations([""]);
        setLocationError(false);
        console.log("Selected location:", location);
        if (type === "pickup") {
            setSessionValue("pickCountry", location.country);
            if (isTestMarket) {
                setSessionValue("pickPlaceId", location.placeId);
                setSessionValue("pickDataSource", location.dataSource);
            }
        }
        else {
            setSessionValue("dropCountry", location.country);
            if (isTestMarket) {
                setSessionValue("dropPlaceId", location.placeId);
                setSessionValue("dropDataSource", location.dataSource);
            }
        }
    };
    const renderLocationOption = (location, key, isChild = false) => (_jsxs("li", { style: { zIndex: 9999, paddingLeft: isChild ? "1rem" : undefined }, className: "dropdown__select-option", role: "option", "aria-selected": "false", onClick: () => handleLocationSelect(location), children: [(location.placeType === "AIRPORT" || location.location_type === Constant.AIRPORT) ? (_jsx(IconAirportBlack, {})) : (location.placeType === "PORT" || location.location_type === Constant.PORT) ? (_jsx(IconPortBlack, {})) : (_jsx(IconLocationBlack, {})), checkTranlation(location)] }, key));
    return (_jsx(_Fragment, { children: _jsxs("div", { className: clsx("searchbar__location", {
                "pickup-location": type === "pickup",
                "return-location": type === "dropoff",
            }), onClick: () => window.innerWidth <= 992 && openModalEvent(type), children: [_jsx("div", { className: `searchbar__location-title ${locationError ? "input-error" : ""}`, id: type === "pickup" ? "pickup_label" : "return_label", children: type === "pickup" ? t("pickup_label") : t("return_label") }), _jsxs("div", { className: "searchbar__location-inner", ref: refDiv, children: [_jsx("div", { className: "searchbar__location-icon", onClick: window.innerWidth > 992
                                ? () => {
                                    setLocations([""]);
                                    setSelectedLocation(["", "", Constant.DEFAULT]);
                                    setIsFocused(true);
                                }
                                : () => {
                                    openModalEvent(type);
                                }, children: selectedLocation[2] === "AIRPORT" || selectedLocation[2] === Constant.AIRPORT ? (_jsx(IconAirportBlack, {})) : selectedLocation[2] === "PORT" || selectedLocation[2] === Constant.PORT ? (_jsx(IconPortBlack, {})) : (_jsx(IconLocationBlack, {})) }), _jsxs("div", { className: "dropdown", style: { width: "100%" }, children: [_jsx("input", { type: "checkbox", className: "dropdown__switch filter-switch", id: type === "pickup" ? "filter-switch" : "filter-switch-1", hidden: true }), _jsx("label", { htmlFor: type === "pickup" ? "filter-switch" : "filter-switch-1", className: "dropdown__options-filter", children: _jsxs("ul", { className: "dropdown__filter", role: "listbox", tabIndex: -1, "aria-label": t("aria_select_location"), onFocus: () => {
                                            setIsFocused(false);
                                        }, onBlur: () => {
                                            setIsFocused(true);
                                        }, children: [_jsx("input", { id: type === "pickup" ? "pickup_location" : "return_location", className: `dropdown__filter-selected d_size_box${locationError ? " input-error" : ""}`, name: "pickup_location_random_12345", autoComplete: "off", autoCorrect: "off", autoCapitalize: "off", spellCheck: "false", onFocus: handleEditSearchBar, type: "text", value: selectedLocation[0], onChange: (e) => {
                                                    setSelectedLocation([e.target.value, selectedLocation[1], selectedLocation[2]]);
                                                    const normalizedCountry = typeof userCountry === "string" && userCountry.trim() !== "" ? userCountry.trim().toUpperCase() : "US";
                                                    handleLocationChange(e, setNoResults, normalizedCountry);
                                                }, placeholder: t("location_input_placeholder"), "aria-label": t("aria_enter_pickup_location") }), _jsxs("li", { children: [_jsx("div", { className: "dropdown__container-scroll limit_dropdown", children: _jsxs("ul", { className: "dropdown__select", children: [_jsx("li", { className: "dropdown__select-option mobile_location", role: "option", "aria-selected": "false", hidden: true, children: _jsx("span", { children: t("use_current_location") }) }, "b"), noResults && (_jsx("li", { className: "dropdown__select-option", role: "row", children: _jsx("span", { children: t("error_location") }) })), locations.map((location, index) => {
                                                                    const children = Array.isArray(location.childrenPlaces) && location.childrenPlaces.length > 0
                                                                        ? location.childrenPlaces
                                                                        : [];
                                                                    const baseKey = location.placeId || location.code || `location-${index}`;
                                                                    return (_jsxs(_Fragment, { children: [renderLocationOption(location, `parent-${baseKey}`), children.map((child, childIndex) => { var _a; return renderLocationOption(child, (_a = child === null || child === void 0 ? void 0 : child._id) !== null && _a !== void 0 ? _a : `child-${baseKey}-${childIndex}`, true); })] }));
                                                                })] }) }), _jsx("div", { onClick: () => {
                                                            setLocations([""]);
                                                            setSelectedLocation(["", "", "1"]);
                                                            setIsFocused(true);
                                                        }, hidden: isFocused, children: _jsx("div", { className: "dropdown__clear-icon", children: _jsx(IconClosed, {}) }) })] }, "a")] }) })] })] })] }) }));
};
export default LocationInputSearchBar;
